import * as React from "react";
import { Card } from "antd";
import { CardContent } from "../../components/antd/card/CardContent";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { tagLinkUrlPrefixes } from "../../services/utils";
import { TagLinksTable } from "../../components/detail/tagLinksTable";
import { Agent, AgentInfo } from "../../schema/types/agent/entity";
import { FormItemDescription } from "../../components/antd/form/FormItemDescription";
import { DOCS_URL } from "../../envVariables";
import { SyntaxHighlight } from "../../components/syntaxHighlight/SyntaxHighlight";
import { agentHelpers } from "../../mst/kinds/agent.helpers";
import { FormItemValue } from "../../components/antd/form/FormItemValue";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { CopyButtonWithApproval } from "../domain/create/copyButtonWithApproval";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpLabelDirtyIndicator } from "../gvc/testNewForm/CpLabelDirtyIndicator";
import { CpTextArea } from "../gvc/testNewForm/CpTextArea";
import { InstanceGrid } from "./components/InstanceGrid";

interface Props {
  agent: Agent;
  agentInfo: AgentInfo[] | undefined;
}
export const Summary: React.FC<Props> = ({ agent, agentInfo }) => {
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
  function toggleDetails() {
    setIsDetailsOpen((x) => !x);
  }

  const isActive = agentHelpers.getIsActive(agent, agentInfo);
  const lastActiveText = agentHelpers.getLastActiveText(agent, agentInfo);
  const envText = agentHelpers.getEnvText(agent, agentInfo);

  const hasInfo = agentInfo && Object.keys(agentInfo).length > 0;
  const hasTagLinks =
    Object.entries(agent.tags || {}).filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
      .length > 0;

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`description`}>
                <FormLabel>Description</FormLabel>
                <CpLabelDirtyIndicator name={`description`} />
              </CpLabel>
              <CpTextArea autoSize name={`description`} style={{ maxWidth: 600 }} />
              <CpErrorWarning name={`description`} />
            </CpFormItem>
            <div className="flex flex-wrap gap-x-32 gap-y-8 mt-4 mb-8">
              <FormItem lastItem>
                <FormLabel>Last Active</FormLabel>
                <FormItemDescription>Recent heartbeat from the agent</FormItemDescription>
                <FormItemValue>
                  <div>
                    <span
                      className={`${
                        isActive ? "bg-color-action pulse" : "bg-color-danger"
                      } rounded-full mr-1 inline-block`}
                      style={{ width: 10, height: 10 }}
                    />
                    <span>{lastActiveText}</span>
                  </div>
                </FormItemValue>
              </FormItem>
              <FormItem lastItem>
                <FormLabel>Protocol Version</FormLabel>
                <FormItemDescription>Agent communication protocol version</FormItemDescription>
                <FormItemValue>
                  <div>{agent.status?.protocolVersion || "v1"}</div>
                </FormItemValue>
              </FormItem>
            </div>
            {hasInfo ? (
              <FormItem>
                <div className="mb-2">
                  <span>See</span>
                  <ExternalLink className="mx-1" url={`${DOCS_URL}/reference/agent`} label="docs" disableCopy />
                  <span>
                    to learn how to set up <span className="font-semibold">Agents</span>.
                  </span>
                </div>
                {agentInfo.some((ai) => Object.keys(ai.env || {}).length > 0) ? (
                  <div className="mb-4">
                    {agent.status?.protocolVersion === "v2" ? (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
                            Instances ({agentInfo.length})
                          </span>
                          <CopyButtonWithApproval value={JSON.stringify(agentInfo, null, 2)} />
                        </div>
                        <InstanceGrid agentInfo={agentInfo} />
                        <div className="mt-4">
                          <div className="flex items-center">
                            <button type={"button"} className="color-link" onClick={toggleDetails}>
                              YAML
                              {isDetailsOpen ? (
                                <MinusOutlined
                                  data-testid="toggle-env"
                                  className="ml-1 feather-icon inline-block"
                                />
                              ) : (
                                <PlusOutlined
                                  data-testid="toggle-env"
                                  className="ml-1 feather-icon inline-block"
                                />
                              )}
                            </button>
                            {isDetailsOpen ? (
                              <CopyButtonWithApproval
                                className="ml-1"
                                value={JSON.stringify(agentInfo || [], null, 2)}
                              />
                            ) : null}
                          </div>
                          {isDetailsOpen ? (
                            <SyntaxHighlight code={envText} language="yaml" showLineNumbers />
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center">
                          <button type={"button"} className="color-link" onClick={toggleDetails}>
                            Details
                            {isDetailsOpen ? (
                              <MinusOutlined data-testid="toggle-env" className="ml-1 feather-icon inline-block" />
                            ) : (
                              <PlusOutlined data-testid="toggle-env" className="ml-1 feather-icon inline-block" />
                            )}
                          </button>
                          {isDetailsOpen ? (
                            <CopyButtonWithApproval
                              className="ml-1"
                              value={JSON.stringify(agentInfo || [], null, 2)}
                            />
                          ) : null}
                        </div>
                        {isDetailsOpen ? <SyntaxHighlight code={envText} language="yaml" showLineNumbers /> : null}
                      </>
                    )}
                  </div>
                ) : null}
              </FormItem>
            ) : null}
            {hasTagLinks ? (
              <TagLinksTable
                tableId="agent-info-tag-links"
                tags={Object.entries(agent.tags || {})
                  .filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
                  .map(([key, value]) => ({ key, value }))}
                styles={{ container: { width: "100%" }, header: { marginBottom: 0 } }}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
